// ============================================
// RÉFÉRENTIEL — agrégation inter-référents (plan §5.3)
// ============================================
// Module PUR. Combine les N contributions de référents en UN référentiel.
// N est VARIABLE : un référent invité qui ne répond jamais est simplement absent
// de l'entrée (pas de N/N strict, cf. §4).

import type { CibleReferentiel } from "../schema";
import type { CibleFourchette } from "./fourchette";
import { estNonConcerne, poidsDeCible } from "./fourchette";
import type { VecteurQsort } from "./qsort";
import { scoreDuNiveau } from "./qsort";

export type StatDimension = { moyenne: number; ecartType: number; n: number };

export type StatFourchette = {
  min: number;
  max: number;
  ecartTypeMin: number;
  ecartTypeMax: number;
  n: number;
  /** Poids moyen des référents : 1 à attente unique, 1 / 0,5 pour les
   *  contributions historiques à importance. Absent des projections
   *  calculées avant la pondération — à lire comme 1. */
  poids?: number;
};

function moyenne(valeurs: number[]): number {
  return valeurs.reduce((a, b) => a + b, 0) / valeurs.length;
}

/** Écart-type de population (pas d'échantillon) : 0 pour un seul référent. */
function ecartType(valeurs: number[]): number {
  if (valeurs.length <= 1) return 0;
  const m = moyenne(valeurs);
  return Math.sqrt(moyenne(valeurs.map((v) => (v - m) ** 2)));
}

/**
 * Moyenne par dimension des vecteurs Q-sort des référents, avec l'écart-type —
 * qui est l'indicateur de CONSENSUS. La moyenne écrase le désaccord par
 * construction : l'écart-type est ce qui le fait remonter (§8.2).
 *
 * Les dimensions absentes d'un vecteur sont ignorées pour ce référent-là (n par
 * dimension), pas comptées comme 0.
 */
export function moyenneQsort(
  vecteurs: VecteurQsort[],
): Record<string, StatDimension> {
  const parCode = new Map<string, number[]>();
  for (const vecteur of vecteurs) {
    for (const [code, score] of Object.entries(vecteur)) {
      const liste = parCode.get(code) ?? [];
      liste.push(score);
      parCode.set(code, liste);
    }
  }
  const resultat: Record<string, StatDimension> = {};
  for (const [code, valeurs] of parCode) {
    resultat[code] = {
      moyenne: moyenne(valeurs),
      ecartType: ecartType(valeurs),
      n: valeurs.length,
    };
  }
  return resultat;
}

/**
 * RECLASSEMENT FORCÉ de l'agrégat dans LA pyramide (§5.1).
 *
 * La moyenne inter-référents est continue : arrondie niveau par niveau, elle
 * produit une pyramide bosselée (trois items « Essentiel », un niveau vide) qui
 * ne se superpose PAS à celle du candidat. Or c'est exactement la superposition
 * qu'on demande à l'œil de faire. Le référentiel est donc reclassé : les items
 * sont ORDONNÉS par score référents, puis versés dans les capacités de la
 * pyramide — même structure que la passation, aux mêmes quotas.
 *
 * Ce que le reclassement coûte : la moyenne 4,7 et la moyenne 4,1 deviennent le
 * même niveau si elles tombent dans le même quota, et deux items séparés de 0,05
 * peuvent atterrir à deux niveaux différents. Ce que l'échelle ipsative rend en
 * échange : deux vecteurs de même total, directement soustractibles, et un score
 * de matching qui ne peut plus contredire la pyramide affichée. La dispersion
 * (`ecartType`) reste portée par chaque item : c'est elle, pas la finesse de la
 * moyenne, qui dit si un placement est consensuel.
 *
 * Départage des ex æquo, dans l'ordre : score décroissant, puis dispersion
 * CROISSANTE (à moyenne égale, l'item sur lequel les référents s'accordent monte
 * — un placement partagé est une attente plus solide qu'un placement moyen issu
 * de contributions opposées), puis ordre des dimensions dans la config. Les trois
 * critères sont déterministes : deux calculs de la même version donnent la même
 * pyramide, sans quoi les scores bougeraient à chaque relecture.
 *
 * `codes` porte l'ordre de la config (dernier départage). Les codes absents de
 * l'agrégat sont ignorés — aucun référent ne les a classés, ils n'entrent pas
 * dans la pyramide (cf. `dimensionsNeutralisees`). Sous distribution forcée côté
 * référent, ce cas ne se produit pas : chaque référent place TOUS les items.
 */
export function niveauxForcesQsort(args: {
  codes: string[];
  agregat: Record<string, StatDimension>;
  pyramide: number[];
}): Record<string, number> {
  const { agregat, pyramide } = args;
  const rang = new Map(args.codes.map((code, i) => [code, i]));

  const classes = args.codes
    .filter((code) => agregat[code] !== undefined)
    .sort((a, b) => {
      const sa = agregat[a]!;
      const sb = agregat[b]!;
      if (sb.moyenne !== sa.moyenne) return sb.moyenne - sa.moyenne;
      if (sa.ecartType !== sb.ecartType) return sa.ecartType - sb.ecartType;
      return rang.get(a)! - rang.get(b)!;
    });

  const niveaux: Record<string, number> = {};
  let niveau = 0;
  let restant = pyramide[0] ?? classes.length;
  for (const code of classes) {
    // Niveau saturé → on descend. La boucle saute les niveaux de capacité nulle
    // et, une fois la pyramide épuisée (agrégat plus large que la pyramide, config
    // invalide), maintient tout le monde au dernier niveau plutôt que d'inventer
    // des niveaux hors échelle.
    while (restant === 0 && niveau < pyramide.length - 1) {
      niveau += 1;
      restant = pyramide[niveau] ?? 0;
    }
    niveaux[code] = niveau;
    if (restant > 0) restant -= 1;
  }
  return niveaux;
}

/**
 * Vecteur de scores de l'entreprise induit par le reclassement forcé — l'exact
 * pendant du vecteur candidat (`vecteurScores`), et ce qui doit servir de cible à
 * TOUT calcul de comparaison : score de matching, corrélation, pôles, écarts par
 * dimension. Comparer un vecteur candidat discret à des moyennes continues
 * laisserait la pyramide affichée et le score se contredire.
 */
export function vecteurEntrepriseForce(
  niveauxForces: Record<string, number>,
  nbNiveaux: number,
): VecteurQsort {
  const vecteur: VecteurQsort = {};
  for (const [code, niveau] of Object.entries(niveauxForces)) {
    vecteur[code] = scoreDuNiveau(niveau, nbNiveaux);
  }
  return vecteur;
}

/**
 * Moyenne des BORNES (pas intersection) des fourchettes des référents.
 *
 * L'intersection produirait un intervalle vide dès que deux référents sont en
 * désaccord franc, et un référentiel vide n'est pas exploitable. La moyenne
 * dégrade proprement ; le désaccord est REPORTÉ par `desaccord()`, pas dissimulé.
 *
 * Les dimensions marquées `nonConcerne` sont EXCLUES du calcul, jamais comptées
 * comme `[0,10]` — sinon un référent sans attente élargirait la cible des autres.
 * Une dimension que TOUS marquent `nonConcerne` est absente du résultat.
 */
export function moyenneFourchettes(
  contributions: Record<string, CibleFourchette>[],
): Record<string, StatFourchette> {
  const mins = new Map<string, number[]>();
  const maxs = new Map<string, number[]>();
  const poids = new Map<string, number[]>();
  for (const contribution of contributions) {
    for (const [code, cible] of Object.entries(contribution)) {
      if (estNonConcerne(cible)) continue;
      mins.set(code, [...(mins.get(code) ?? []), cible.min]);
      maxs.set(code, [...(maxs.get(code) ?? []), cible.max]);
      poids.set(code, [...(poids.get(code) ?? []), poidsDeCible(cible)]);
    }
  }
  const resultat: Record<string, StatFourchette> = {};
  for (const [code, valeursMin] of mins) {
    const valeursMax = maxs.get(code)!;
    resultat[code] = {
      min: moyenne(valeursMin),
      max: moyenne(valeursMax),
      ecartTypeMin: ecartType(valeursMin),
      ecartTypeMax: ecartType(valeursMax),
      n: valeursMin.length,
      // Moyenne des poids : à un seul référent (le cas PAMI) c'est exactement
      // le sien (toujours 1 à attente unique). À plusieurs, deux référents « forte » et
      // « faible » donnent 2 — la même dimension qu'un « moyenne » unanime.
      poids: moyenne(poids.get(code)!),
    };
  }
  return resultat;
}

/**
 * Dimensions sur lesquelles au moins deux référents proposent des intervalles
 * DISJOINTS. Si deux référents disent `[2,4]` et `[7,9]`, la moyenne `[4.5,6.5]`
 * ne représente personne : la restitution doit le dire.
 */
export function desaccord(
  contributions: Record<string, CibleFourchette>[],
): string[] {
  const parCode = new Map<string, { min: number; max: number }[]>();
  for (const contribution of contributions) {
    for (const [code, cible] of Object.entries(contribution)) {
      if (estNonConcerne(cible)) continue;
      parCode.set(code, [...(parCode.get(code) ?? []), cible]);
    }
  }
  const codes: string[] = [];
  for (const [code, intervalles] of parCode) {
    const disjoints = intervalles.some((a, i) =>
      intervalles.some((b, j) => j > i && (a.max < b.min || b.max < a.min)),
    );
    if (disjoints) codes.push(code);
  }
  return codes;
}

/**
 * Fiabilité du référentiel. Seuil d'exploitabilité à 3 référents (§4) : en
 * dessous, la projection est calculée quand même — une campagne à 1 référent
 * reste légitime dans une petite structure — mais marquée `faible` et la
 * restitution affiche un avertissement. Jamais de blocage.
 */
export const SEUIL_REFERENTS_FIABLE = 3;

/**
 * Le PAMI est un référentiel À RÉFÉRENT UNIQUE : une seule personne le remplit,
 * par construction. Lui appliquer le seuil de 3 marquerait TOUJOURS `faible` et
 * l'avertissement deviendrait du bruit — un avertissement permanent n'avertit
 * plus de rien.
 */
export const SEUIL_REFERENTS_FIABLE_PAMI = 1;

export function seuilReferentsFiable(cible: CibleReferentiel): number {
  return cible === "pami"
    ? SEUIL_REFERENTS_FIABLE_PAMI
    : SEUIL_REFERENTS_FIABLE;
}

export function fiabilite(
  nbReferents: number,
  nbDimensionsEnDesaccord = 0,
  seuil: number = SEUIL_REFERENTS_FIABLE,
): "faible" | "correcte" {
  if (nbReferents < seuil) return "faible";
  if (nbDimensionsEnDesaccord > 0) return "faible";
  return "correcte";
}
